# Delete your Wellness Frontier account

**Effective date:** September 1, 2026  
**Last updated:** October 26, 2026

This page explains how to ask us to delete your Wellness Frontier account and
the information we hold about you. You do not need the app, and you do not need
to sign in, to make the request.

Wellness Frontier is operated by Advanced Health and Wellness Group. Our full
**Privacy Policy** describes everything we collect and why; this page covers
deletion only.

## How to ask

There are two routes, and either one is enough.

- **In the app.** Open the **Account** area and choose the delete option.
- **By email.** Write to **privacy@wellnessfrontier.ai** and ask us to delete
  your account. Send it from the work email address you use with Wellness
  Frontier if you can, because it helps us find your record.

## How we check it is you

Before anything is deleted we email you a confirmation code. The code is good
for 24 hours and works once. Deletion begins only after you enter it.

This step exists so that nobody else can delete your account, and it is the
reason we cannot act on a request we cannot connect to a real mailbox.

## What is deleted

- your profile information;
- your private survey answers;
- your connected-health measurements, such as steps, sleep and resting heart
  rate;
- your goals and the permissions you gave to connected health sources;
- your coach notes and your daily AI trainer chat count; and
- every signed-in session, ended immediately, on every device.

You also receive a deletion receipt with a reference number. The receipt does
not name you.

## What your employer can see

We do not tell your employer that you deleted your account. Deleting it takes
your name off your employer's staff list, and your employer can see that you
are no longer on the list. We do not show your employer a reason or a date.
Your name and points can stay on your employer's points board until we next
rebuild the board, which we do once a day.

## What is not deleted, and why

Deletion does not reach information that is no longer connected to you, or that
we are required to keep.

- **Group totals that have already been published.** These are counts for a
  whole department or company. They do not contain your identity.
- **De-identified questions used to improve the health coach.** These are no
  longer linked to you, so a request to delete your information cannot find
  them.
- **Audit and security records.** Those kept with your personal information are
  generally kept for up to 400 days. A separate log on our program server is
  not deleted yet. It records when our platform staff or your employer's admin
  viewed or changed your entry. It holds your account reference, which is a
  scrambled code and not your name. Section 7 of our Privacy Policy lists what
  an entry holds.
- **Backups.** Where a backup holds a copy, it is removed on the normal backup
  rotation or when that backup is next restored, within the period the law
  allows.
- **Copies Microsoft keeps.** If you used the coach or the AI trainer,
  Microsoft may keep a copy of what was sent to it for a limited period, to
  detect misuse. It is Microsoft's copy, not ours, so we cannot delete it.

We also keep two deletion markers for a short time, one on each of our two
servers. They stop a staff file your employer sent before you deleted your
account from creating the account again. Each holds your employer, a random
value, a scrambled code made from that value and your account reference, and
the day after you deleted your account. Neither holds your name or your email
address. While we hold them, we can use them to tell that your account was
deleted, and on which day. Your employer cannot read them. A daily job deletes
each one once 45 days have passed since the day that follows your deletion.

If your employer lists you in a staff file on a later day, a new, empty account
is created for you.

## How long it takes

Your sessions end straight away. The rest is completed as quickly as we can, and
always within the time the law where you live requires. If we cannot act on a
request, we will tell you why and how to appeal.

## Leaving the program is not the same as deleting

You can stop using Wellness Frontier, or disconnect a health source, without
deleting anything. Disconnecting a source stops new information arriving; it
does not remove what was already sent. Use this page if you want the existing
information removed as well.

## Questions

Write to **privacy@wellnessfrontier.ai**. Your rights, including access,
correction and appeal, are described in full in our Privacy Policy.
